import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Linkedin, Check, X } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Button } from '../../../components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../../../components/ui/avatar';
import type { PersonProfile, PrefillPersonCandidate } from '../../../shared/api/setupWizardApi';
import type { JobFunction, JobLevel } from './options';
import { JobRoleFields } from './JobRoleFields';
import { WizardAvatarPicker } from './WizardAvatarPicker';

export interface ProfileFormState {
  jobFunction: JobFunction | '';
  jobFunctionCustom: string;
  jobLevel: JobLevel | '';
  linkedinUrl: string;
  headline: string;
  photoUrl: string | null;
  avatarFileId: string | null;
  /**
   * A photo the user picked in the wizard, held until the tenant exists.
   * `files.tenant_id` is NOT NULL, so a `pending_setup` user cannot upload;
   * `SetupWizardPageV2` uploads this right after `createTenant` succeeds.
   */
  photoFile: File | null;
}

interface ProfileStepProps {
  firstName: string;
  lastName: string;
  email: string;
  gravatarUrl: string | null;
  candidates: PrefillPersonCandidate[];
  bestCandidateId: string | null;
  value: ProfileFormState;
  onChange: (patch: Partial<ProfileFormState>) => void;
  onConfirmPerson: (linkedinUrl: string, snippetHint: string | null) => Promise<PersonProfile>;
}

export function ProfileStep({
  firstName,
  lastName,
  email,
  gravatarUrl,
  candidates,
  bestCandidateId,
  value,
  onChange,
  onConfirmPerson,
}: ProfileStepProps) {
  const { t } = useTranslation(['user_management', 'common']);

  // Order candidates so the best match is first.
  const ordered = [...candidates].sort((a, b) => {
    if (a.linkedin_url === bestCandidateId) return -1;
    if (b.linkedin_url === bestCandidateId) return 1;
    return b.raw_score - a.raw_score;
  });

  const [candidateIndex, setCandidateIndex] = useState(0);
  const [confirmed, setConfirmed] = useState(false);
  const [showPaste, setShowPaste] = useState(false);
  const [pasteUrl, setPasteUrl] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const activeCandidate = ordered[candidateIndex] ?? null;

  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || '?';
  const photoSrc = value.photoUrl || gravatarUrl || activeCandidate?.photo_thumb_url || undefined;

  const applyProfile = (profile: PersonProfile) => {
    onChange({
      linkedinUrl: profile.linkedin_url,
      headline: profile.headline ?? '',
      photoUrl: profile.photo_url,
      avatarFileId: profile.photo_file_id,
    });
    setConfirmed(true);
    setShowPaste(false);
  };

  const handleConfirm = async (linkedinUrl: string, snippetHint: string | null) => {
    setConfirming(true);
    setConfirmError(null);
    try {
      const profile = await onConfirmPerson(linkedinUrl, snippetHint);
      applyProfile(profile);
    } catch {
      setConfirmError(t('setupWizardV2.profile.confirmError'));
    } finally {
      setConfirming(false);
    }
  };

  const handleNotMe = () => {
    setConfirmError(null);
    if (candidateIndex < ordered.length - 1) {
      setCandidateIndex((i) => i + 1);
    } else {
      setShowPaste(true);
    }
  };

  const showCandidateCard = !confirmed && !showPaste && activeCandidate;
  // With no candidate there is no card to host the "Paste my LinkedIn URL"
  // action, which is the common case for people with no public LinkedIn match.
  // Surface the paste affordance on its own so it stays reachable.
  const showAddLinkedIn = !confirmed && !showPaste && !activeCandidate;

  return (
    <div className="space-y-6">
      {/* Avatar + read-only name confirm */}
      <div className="flex flex-col items-center gap-3 text-center">
        <WizardAvatarPicker
          initials={initials}
          fullName={`${firstName} ${lastName}`.trim() || email}
          photoUrl={photoSrc ?? null}
          file={value.photoFile}
          onFileChange={(photoFile) => onChange({ photoFile })}
        />
        <div>
          <p className="text-base font-semibold text-foreground">
            {`${firstName} ${lastName}`.trim() || email}
          </p>
          <p className="text-sm text-muted-foreground">{email}</p>
        </div>
      </div>

      {/* LinkedIn candidate card */}
      {showCandidateCard && (
        <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-4">
          <div className="flex items-start gap-3">
            <Avatar className="h-12 w-12 flex-shrink-0">
              {activeCandidate.photo_thumb_url && (
                <AvatarImage src={activeCandidate.photo_thumb_url} alt="" />
              )}
              <AvatarFallback>
                <Linkedin className="h-5 w-5 text-muted-foreground" />
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                <Linkedin className="h-4 w-4 flex-shrink-0 text-[#0a66c2]" aria-hidden="true" />
                <span className="truncate">{t('setupWizardV2.profile.candidateTitle')}</span>
              </div>
              <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">
                {activeCandidate.headline_snippet}
              </p>
            </div>
          </div>
          {confirmError && <p className="text-xs text-destructive">{confirmError}</p>}
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              className="h-11 gap-1.5"
              disabled={confirming}
              data-testid="confirm-person-yes"
              onClick={() =>
                handleConfirm(activeCandidate.linkedin_url, activeCandidate.headline_snippet)
              }
            >
              {confirming ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              {t('setupWizardV2.profile.yesThatsMe')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-11 gap-1.5"
              disabled={confirming}
              onClick={handleNotMe}
            >
              <X className="h-4 w-4" />
              {t('setupWizardV2.profile.notMe')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-11"
              disabled={confirming}
              onClick={() => setShowPaste(true)}
            >
              {t('setupWizardV2.profile.pasteOwn')}
            </Button>
          </div>
        </div>
      )}

      {/* No candidate to confirm — offer the paste-your-own path directly */}
      {showAddLinkedIn && (
        <Button
          type="button"
          variant="outline"
          className="h-11 w-full gap-1.5"
          onClick={() => setShowPaste(true)}
          data-testid="add-linkedin"
        >
          <Linkedin className="h-4 w-4 flex-shrink-0 text-[#0a66c2]" aria-hidden="true" />
          {t('setupWizardV2.profile.addLinkedin')}
        </Button>
      )}

      {/* Paste-your-own LinkedIn URL */}
      {!confirmed && showPaste && (
        <div className="space-y-2 rounded-lg border border-border p-4">
          <Label htmlFor="v2-linkedin-paste">{t('setupWizardV2.profile.linkedinLabel')}</Label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              id="v2-linkedin-paste"
              type="url"
              value={pasteUrl}
              onChange={(e) => setPasteUrl(e.target.value)}
              placeholder={t('setupWizardV2.profile.linkedinPlaceholder')}
              className="flex-1"
            />
            <Button
              type="button"
              className="h-11"
              disabled={confirming || pasteUrl.trim().length === 0}
              onClick={() => handleConfirm(pasteUrl.trim(), null)}
            >
              {confirming && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
              {t('setupWizardV2.profile.lookUp')}
            </Button>
          </div>
          {confirmError && <p className="text-xs text-destructive">{confirmError}</p>}
        </div>
      )}

      {/* Confirmed profile summary */}
      {confirmed && (
        <div className="rounded-lg border border-success/30 bg-success/5 p-4">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Check className="h-4 w-4 text-success" />
            {value.headline || t('setupWizardV2.profile.linkedinLinked')}
          </div>
          {value.linkedinUrl && (
            <p className="mt-1 truncate text-xs text-muted-foreground">{value.linkedinUrl}</p>
          )}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="mt-2 h-9 px-2"
            onClick={() => {
              setConfirmed(false);
              setShowPaste(true);
              onChange({ linkedinUrl: '', headline: '', photoUrl: null, avatarFileId: null });
            }}
          >
            {t('setupWizardV2.profile.change')}
          </Button>
        </div>
      )}

      {/* Job function + level (shared with the joiner Flow B profile) */}
      <JobRoleFields value={value} onChange={onChange} />
    </div>
  );
}
